<template>
  <div :class="wrapperClasses">
    <input
        :type="type"
        :value="modelValue"
        @input="updateValue"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.enter="$emit('enter', $event)"
        class="input-field"
        :placeholder="placeholder || ' '"
        :maxLength="maxLength"
        :disabled="disabled"
        :readonly="readonly"
        ref="inputRef"
    />
    <label class="input-label">{{ label }}</label>

    <span v-if="icon && !loading" class="input-icon">{{ icon }}</span>

    <span v-if="maxLength && !loading" class="char-counter">
      {{ currentLength }}/{{ maxLength }}
    </span>

    <div v-if="helperText" class="helper-text">
      {{ helperText }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'BeautifulInput',
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    type: {
      type: String,
      default: 'text',
      validator: (value) => {
        return ['text', 'email', 'password', 'number', 'tel', 'url', 'search'].includes(value)
      }
    },
    icon: {
      type: String,
      default: ''
    },
    helperText: {
      type: String,
      default: ''
    },
    maxLength: {
      type: Number,
      default: null
    },
    state: {
      type: String,
      default: null,
      validator: (value) => {
        return value === null || ['error', 'success'].includes(value)
      }
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue', 'input', 'focus', 'blur', 'enter'],
  data() {
    return {
      isFocused: false
    }
  },
  computed: {
    currentLength() {
      return this.modelValue ? this.modelValue.length : 0
    },
    wrapperClasses() {
      return [
        'beautiful-input-wrapper',
        this.state ? `state-${this.state}` : '',
        {
          'has-icon': this.icon,
          'has-counter': this.maxLength,
          'has-value': this.modelValue,
          'is-loading': this.loading,
          'is-focused': this.isFocused,
          'is-disabled': this.disabled,
          'is-readonly': this.readonly
        }
      ]
    }
  },
  methods: {
    updateValue(event) {
      const value = event.target.value
      this.$emit('update:modelValue', value)
      this.$emit('input', value)
    },
    handleFocus(event) {
      this.isFocused = true
      this.$emit('focus', event)
    },
    handleBlur(event) {
      this.isFocused = false
      this.$emit('blur', event)
    },
    focus() {
      this.$refs.inputRef.focus()
    },
    blur() {
      this.$refs.inputRef.blur()
    }
  }
}
</script>

<style scoped>
/* Design System Colors */
.beautiful-input-wrapper {
  position: relative;
  width: 100%;
}

.input-field {
  width: 100%;
  height: 40px;
  padding: 0 12px;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  background: white;
  border: 1px solid #D1D5DB;
  border-radius: 8px;
  outline: none;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  line-height: normal;
  font-family: inherit;
}

.input-field::placeholder {
  color: #9CA3AF;
  opacity: 0;
  transition: opacity 0.2s ease;
}

.input-field:focus::placeholder {
  opacity: 1;
}

.input-field:focus {
  background: white;
  border-color: #2563EB;
  border-width: 1.5px;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
}

.input-field:hover:not(:focus):not(:disabled) {
  border-color: #9CA3AF;
}

/* Floating Label */
.input-label {
  position: absolute;
  left: 12px;
  top: 20px;
  transform: translateY(-50%);
  color: #6B7280;
  font-size: 14px;
  font-weight: 400;
  pointer-events: none;
  transition: all 0.2s ease;
  background: transparent;
  padding: 0 4px;
  line-height: 1;
  display: flex;
  align-items: center;
}

.input-field:focus ~ .input-label,
.input-field:not(:placeholder-shown) ~ .input-label {
  top: 0;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 500;
  color: #2563EB;
  background: white;
}

.beautiful-input-wrapper.has-value .input-label {
  top: 0;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 500;
  background: white;
}

/* Icon Support */
.beautiful-input-wrapper.has-icon .input-field {
  padding-left: 40px;
  padding-right: 12px;
}

.beautiful-input-wrapper.has-icon .input-label {
  left: 40px;
}

.beautiful-input-wrapper.has-icon .input-field:focus ~ .input-label,
.beautiful-input-wrapper.has-icon .input-field:not(:placeholder-shown) ~ .input-label,
.beautiful-input-wrapper.has-icon.has-value .input-label {
  left: 12px;
}

.input-icon {
  position: absolute;
  left: 12px;
  top: 20px;
  transform: translateY(-50%);
  width: 18px;
  height: 18px;
  color: #9CA3AF;
  transition: color 0.2s ease;
  pointer-events: none;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.input-field:focus ~ .input-icon {
  color: #2563EB;
}

/* Helper Text */
.helper-text {
  margin-top: 4px;
  font-size: 11px;
  color: #6B7280;
  transition: color 0.2s ease;
  padding: 0 4px;
}

/* Character Counter */
.char-counter {
  position: absolute;
  right: 12px;
  top: 20px;
  transform: translateY(-50%);
  font-size: 11px;
  color: #9CA3AF;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.beautiful-input-wrapper.has-counter .input-field {
  padding-right: 50px;
}

.beautiful-input-wrapper.has-icon.has-counter .input-field {
  padding-left: 40px;
  padding-right: 50px;
}

/* Error State */
.beautiful-input-wrapper.state-error .input-field {
  border-color: #EF4444;
  background: white;
}

.beautiful-input-wrapper.state-error .input-field:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.beautiful-input-wrapper.state-error .input-label {
  color: #EF4444;
}

.beautiful-input-wrapper.state-error .helper-text {
  color: #EF4444;
}

.beautiful-input-wrapper.state-error .input-icon {
  color: #EF4444;
}

/* Success State */
.beautiful-input-wrapper.state-success .input-field {
  border-color: #10B981;
  background: white;
}

.beautiful-input-wrapper.state-success .input-field:focus {
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.beautiful-input-wrapper.state-success .input-label {
  color: #10B981;
}

.beautiful-input-wrapper.state-success .helper-text {
  color: #10B981;
}

.beautiful-input-wrapper.state-success .input-icon {
  color: #10B981;
}

/* Loading State */
@keyframes spin {
  to { transform: translateY(-50%) rotate(360deg); }
}

.beautiful-input-wrapper.is-loading::after {
  content: '';
  position: absolute;
  right: 12px;
  top: 20px;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  border: 2px solid #2563EB;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 0.6s linear infinite;
}

.beautiful-input-wrapper.is-loading .char-counter {
  display: none;
}

/* Disabled State */
.beautiful-input-wrapper.is-disabled .input-field {
  background: #F9FAFB;
  color: #9CA3AF;
  cursor: not-allowed;
  border-color: #E5E7EB;
}

.beautiful-input-wrapper.is-disabled .input-label {
  color: #9CA3AF;
  background: #F9FAFB;
}

.beautiful-input-wrapper.is-disabled .input-icon {
  color: #D1D5DB;
}

/* Readonly State */
.beautiful-input-wrapper.is-readonly .input-field {
  background: #F9FAFB;
  cursor: default;
}

/* Focus visible for accessibility */
.input-field:focus-visible {
  outline: none;
}

/* Responsive - prevent zoom on iOS */
@media (max-width: 640px) {
  .input-field {
    font-size: 16px;
    height: 44px;
  }
}
</style>